/**
 * AddToGroupDialog — Add a single user to one or more groups.
 *
 * Wires the picker to live `useUserGroups()` (the same source the Groups
 * tab uses). On submit, loops `useAddGroupMembers` per selected group so
 * the underlying single-group endpoint is fanned out client-side until a
 * batch endpoint exists. Result toasts roll up "added vs already members"
 * across all calls.
 *
 * No per-user-groups endpoint exists yet (CM-4 in the audit findings) so
 * we cannot pre-filter the picker by current memberships. Already-member
 * detection happens server-side and lands in the `alreadyMembers` field
 * of each response — surfaced in the toast so the operator understands
 * why a "selected" group didn't get added.
 *
 * Reason field is captured for the audit trail (CMMC 3.3.2 / SOC 2 CC6.3)
 * and currently held client-side; surfacing it on the audit row is
 * tracked as a follow-up to the underlying endpoint.
 */

import { useState, useEffect } from 'react';
import {
  UsersRound, Check, Sparkles, AlertCircle, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useUserGroups,
  useAddGroupMembers,
  type UserGroupRow,
} from '@/lib/use-users';
import type { User } from '@/lib/mock-users-data';

export interface AddToGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onAdded: (user: User, addedGroupIds: string[], reason: string) => void;
}

export function AddToGroupDialog({ open, onOpenChange, user, onAdded }: AddToGroupDialogProps) {
  const { groups: tenantGroups, isLoading: groupsLoading } = useUserGroups({ pageSize: 200 });
  const addMembers = useAddGroupMembers();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setReason('');
      setIsSubmitting(false);
    }
  }, [open]);

  if (!user) return null;

  // Manual groups are addable; dynamic groups are auto-populated by the
  // server-side query and shown read-only for context.
  const manualOptions = tenantGroups.filter((g) => g.membershipType === 'manual');
  const dynamicOptions = tenantGroups.filter((g) => g.membershipType === 'dynamic');

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canSave = selectedIds.size > 0 && reason.trim().length >= 5 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSave) return;
    setIsSubmitting(true);
    const ids = Array.from(selectedIds);
    // Fan out one POST per group. Promise.allSettled so a single failure
    // (privilege, deleted-group race) doesn't roll back the rest.
    const results = await Promise.allSettled(
      ids.map((groupId) =>
        addMembers.mutateAsync({ groupId, body: { userIds: [user.id] } }),
      ),
    );
    const added: string[] = [];
    const already: string[] = [];
    const failed: string[] = [];
    results.forEach((r, i) => {
      const groupId = ids[i];
      if (r.status === 'rejected') {
        failed.push(groupId);
      } else if (r.value.added.includes(user.id)) {
        added.push(groupId);
      } else if (r.value.alreadyMembers.includes(user.id)) {
        already.push(groupId);
      }
    });

    if (added.length > 0) {
      const parts: string[] = [`${user.fullName} added to ${added.length} group${added.length === 1 ? '' : 's'}`];
      if (already.length > 0) parts.push(`already in ${already.length}`);
      if (failed.length > 0) parts.push(`${failed.length} failed`);
      toast.success(parts.join(', '), { description: 'Audit event recorded.' });
      onAdded(user, added, reason.trim());
      onOpenChange(false);
    } else if (already.length > 0 && failed.length === 0) {
      toast.info(`${user.fullName} was already in all selected group${already.length === 1 ? '' : 's'}`);
      onOpenChange(false);
    } else {
      // All requests failed — surface the first error message if available.
      const firstFailure = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
      const message = firstFailure ? (firstFailure.reason as Error)?.message ?? 'Could not add to group' : 'Could not add to group';
      toast.error(message);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <UsersRound className="h-5 w-5 text-primary" />
            Add to groups
          </DialogTitle>
          <DialogDescription>
            Add {user.fullName} to groups. Group memberships grant additional roles and resource access.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Available groups {manualOptions.length > 0 && `(${manualOptions.length})`}
              </p>
              {groupsLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading groups…
                </div>
              ) : manualOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">
                  No groups defined for this tenant. Create one in <span className="font-mono">Users → Groups → New Group</span>.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {manualOptions.map((g) => (
                    <GroupOption
                      key={g.id}
                      group={g}
                      selected={selectedIds.has(g.id)}
                      onToggle={() => toggle(g.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {dynamicOptions.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Dynamic groups (auto-assigned)
                </p>
                <div className="space-y-1.5">
                  {dynamicOptions.map((g) => (
                    <GroupOption key={g.id} group={g} selected={false} onToggle={() => {}} readOnly dynamic />
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Dynamic groups recompute nightly from the server-side query — they cannot be assigned manually.
                </p>
              </div>
            )}

            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs font-semibold">
                Reason for change <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Added to compliance reviewers for Q3 audit prep"
                rows={3}
                className="text-xs"
                disabled={isSubmitting}
              />
              <p className="text-[10px] text-muted-foreground">
                Captured for the audit trail (CMMC 3.3.2 / SOC 2 CC6.3). Minimum 5 characters.
              </p>
            </div>

            {selectedIds.size > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  Adding to {selectedIds.size} group{selectedIds.size === 1 ? '' : 's'} may grant new roles and resource access.
                  Effective permissions recompute on user's next request.
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-muted-foreground">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!canSave}>
                {isSubmitting ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Adding…</>
                ) : (
                  <><Check className="h-4 w-4 mr-1.5" /> Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}</>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupOption({
  group, selected, onToggle, readOnly = false, dynamic = false,
}: {
  group: UserGroupRow;
  selected: boolean;
  onToggle: () => void;
  readOnly?: boolean;
  dynamic?: boolean;
}) {
  const Icon = dynamic ? Sparkles : UsersRound;
  return (
    <label
      className={cn(
        'flex items-start gap-2 rounded-md border p-2.5 transition-colors',
        readOnly ? 'cursor-default opacity-70' : 'cursor-pointer',
        selected && !readOnly ? 'bg-primary/5 border-primary/30' : !readOnly && 'hover:bg-muted/50',
      )}
    >
      <Checkbox checked={selected} onCheckedChange={readOnly ? undefined : onToggle} disabled={readOnly} className="mt-0.5" />
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-medium">{group.name}</span>
          <Badge variant="outline" className={cn(
            'text-[9px] capitalize',
            dynamic
              ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30'
              : 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
          )}>
            {group.membershipType}
          </Badge>
          <Badge variant="outline" className="text-[9px] tabular-nums">{group.memberCount} member{group.memberCount === 1 ? '' : 's'}</Badge>
        </div>
        {group.description && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{group.description}</p>
        )}
      </div>
    </label>
  );
}
